import { useEffect, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Button,
  Chip,
  ColorsV2,
  Divider,
  Flex,
  InitialsInCircle,
  Header,
  Icon,
  SearchableMultiSelectField,
  Switch,
  Text,
  getToken,
  sizes,
  spaces,
  useThemeColors,
} from '@electricjs/arc';
import {
  EmployeeStatusEnum,
  RequestStatusEnum,
} from '@electricjs/core_entity-client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import appIcon from '@/assets/app.svg';
import laptopIcon from '@/assets/laptop.svg';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useOrganizationEmployees } from '@/hooks/useOrganizationEmployees';
import { useUpdateRequestStatus } from '@/hooks/useUpdateRequestStatus';
import { RequestTypeCategoryNameEnum } from '@/hooks/useGetRequestTypeCategoryChampionEmployee';
import { AddEmployeeParams } from '@/pages/Onboardings/NewOnboarding';
import { useBulkUpdateEmployeesMutation } from '@/redux/slices/employeeApiSlice';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { EmployeeSelectOption } from '@/components/EmployeeSelectOption';
import { Group } from '@/types/groups';
import formatDate from '@/helpers/formatDate';
import type { IconNames } from '@electricjs/arc';
import { getFullName } from 'common/utils/getFullName';
import CheckBoxTile from './CheckBoxTile';
import { FormActionsContainer } from './common';
import useIsRequestTypeCategoryChampion from './hooks/useIsRequestTypeCategoryChampion';
import { NoWorkEmailBanner } from './NoWorkEmailBanner';
import {
  ITSetupOption,
  LicenseAction,
  OnboardingFormData,
  OnboardingStep,
} from './types';

type OnboardingProps = {
  addEmployee: (params: AddEmployeeParams) => void;
  submitOnboarding: (
    employeeId: string,
    onSuccess: () => void,
    needsHardware?: boolean,
    needsApplications?: boolean,
    watchers?: string[]
  ) => void;
  existingEmployeeId?: string;
  hrisOnboardingRequestId?: string;
  isMultiLicense: boolean;
  disableNext: boolean;
  handleSetInactive: () => void;
  existingEmployeeGroups?: Group[];
  isApplicationAutomationEnabled?: boolean;
};

const Onboarding = ({
  addEmployee,
  submitOnboarding,
  existingEmployeeId,
  hrisOnboardingRequestId = '',
  isMultiLicense,
  disableNext,
  handleSetInactive,
  existingEmployeeGroups,
  isApplicationAutomationEnabled = false,
}: OnboardingProps) => {
  const { t } = useTranslation('common');
  const { ithNewItSetup } = useFlags();
  const organizationId = useGetOrganizationId();
  const navigate = useNavigate();
  const { control, setValue } = useFormContext<OnboardingFormData>();
  const { isValid, isSubmitting } = useFormState();
  const [
    needsApplications,
    needsHardware,
    firstName,
    lastName,
    email,
    organizationPackageId,
    watchers,
  ] = useWatch({
    control,
    name: [
      'needsApplications',
      'needsHardware',
      'demographics.firstName',
      'demographics.lastName',
      'employeeInformation.email',
      'organizationPackageId',
      'watchers',
    ],
  });

  const isCurrentUserHardwareChampion = useIsRequestTypeCategoryChampion(
    RequestTypeCategoryNameEnum.DEVICE
  );

  const { space_m } = spaces;

  const [bulkUpdateEmployees, { isLoading: isLoadingEmployees }] =
    useBulkUpdateEmployeesMutation();
  const { updateRequest, updateRequestLoading } = useUpdateRequestStatus({
    requestId: hrisOnboardingRequestId,
  });

  const isBlockedByMissingEmail = !!existingEmployeeId && !email;

  const fullName = getFullName(firstName, lastName);
  const needsOnboarding = needsApplications || needsHardware;
  const nextButtonLabel = needsOnboarding
    ? t('onboardings.onboarding.nextButton')
    : t('onboardings.onboarding.setEmployeeActiveButton');
  const disableNextButton = !isValid || disableNext || isBlockedByMissingEmail;

  const handlePrevious = () => {
    if (isMultiLicense) {
      setValue('currentStep', OnboardingStep.LicenseSelection);
    } else {
      setValue('currentStep', OnboardingStep.EmployeeInformation);
    }
  };

  // If the employee does not need onboarding, navigate to the employee's profile page.
  // If the employee needs applications, navigate to the next step, where the onboarding will be created after adding the employee to the groups.
  // If the employee needs only hardware, create the onboarding and navigate to the hardware procurement page.
  const handleNextNavigation = async (employeeId: string) => {
    const employeeProfileUrl = `/employees/${employeeId}`;
    const hardwareStoreUrl = `/get-hardware?${SelectionParamKeys.EmployeeId}=${employeeId}`;
    const newUrl = isCurrentUserHardwareChampion
      ? hardwareStoreUrl
      : employeeProfileUrl;

    if (!employeeId) {
      return;
    }
    if (!needsOnboarding) {
      // If the HRIS-ingested employee doesn't need hardware or apps, set them as active

      if (existingEmployeeId) {
        await bulkUpdateEmployees({
          organizationId,
          employees: [
            {
              id: existingEmployeeId,
              status: EmployeeStatusEnum.Active,
              // If the organization has multiple packages, the selected package id is used.
              // Otherwise, the package id is undefined and the default value will be set in the backend.
              organizationPackageId,
            },
          ],
        });
      }

      // If it exists, mark HRIS onboarding request as completed
      if (hrisOnboardingRequestId) {
        await updateRequest({
          newStatus: RequestStatusEnum.Completed,
        });
      }

      navigate(employeeProfileUrl);
      return;
    }
    if (needsApplications) {
      setValue('currentStep', OnboardingStep.Applications);
    } else {
      submitOnboarding(
        employeeId,
        () => navigate(newUrl),
        true,
        false,
        watchers
      );
    }
  };

  const handleNext = () => {
    if (!existingEmployeeId) {
      // If the employee doesn't already exist, add them
      addEmployee({
        onSuccess: (employeeId: string) => {
          handleNextNavigation(employeeId);
        },
        status: EmployeeStatusEnum.Active,
        // If the organization has multiple packages, the selected package id is used.
        // Otherwise, the package id is undefined and the default value will be set in the backend.
        organizationPackageId,
      });
    } else {
      handleNextNavigation(existingEmployeeId);
    }
  };

  if (isBlockedByMissingEmail) {
    return <NoWorkEmailBanner employeeName={fullName} />;
  }

  if (ithNewItSetup || isApplicationAutomationEnabled) {
    return (
      <NewITSetup
        fullName={fullName}
        existingEmployeeId={existingEmployeeId}
        existingEmployeeGroups={existingEmployeeGroups}
        isMultiLicense={isMultiLicense}
        organizationPackageId={organizationPackageId}
        hrisOnboardingRequestId={hrisOnboardingRequestId}
        disableNext={disableNext}
        addEmployee={addEmployee}
        submitOnboarding={submitOnboarding}
        isApplicationAutomationEnabled={isApplicationAutomationEnabled}
      />
    );
  }

  return (
    <>
      <Header
        title={t('onboardings.onboarding.title', { fullName })}
        subtitle={t('onboardings.onboarding.subtitle')}
      />
      <Flex mt="6rem" gap="3.5rem" flexWrap="wrap">
        <Flex flexDirection="column" gap="1.2rem" width="24.8rem">
          <CheckBoxTile
            text={t('onboardings.onboarding.applicationsLabel')}
            icon={appIcon}
            name="needsApplications"
          />
          <Text variant="body-2" px={space_m}>
            {t('onboardings.onboarding.applicationsDescription')}
          </Text>
        </Flex>
        <Flex flexDirection="column" gap="1.2rem" width="24.8rem">
          <CheckBoxTile
            text={t('onboardings.onboarding.hardwareLabel')}
            icon={laptopIcon}
            name="needsHardware"
          />
          <Text variant="body-2" px={space_m}>
            {t('onboardings.onboarding.hardwareDescription')}
          </Text>
        </Flex>
      </Flex>
      <FormActionsContainer>
        <Button
          id="onboarding-previous-button"
          variant="outline"
          onClick={handlePrevious}
          disabled={isSubmitting || !!existingEmployeeId}>
          {t('onboardings.onboarding.previousButton')}
        </Button>
        <Flex gap="2rem">
          {!needsOnboarding && (
            <Button
              id="onboarding-set-as-inactive-button"
              variant="text"
              onClick={handleSetInactive}
              disabled={
                isSubmitting || updateRequestLoading || isLoadingEmployees
              }>
              {t('onboardings.onboarding.setAsInactiveButton')}
            </Button>
          )}
          <Button
            id="onboarding-next-button"
            onClick={handleNext}
            loading={isSubmitting || updateRequestLoading || isLoadingEmployees}
            disabled={disableNextButton}>
            {nextButtonLabel}
          </Button>
        </Flex>
      </FormActionsContainer>
    </>
  );
};

// ---------------------------------------------------------------------------
// Redesigned IT Setup screen (gated by the `ithNewItSetup` LD flag)
// ---------------------------------------------------------------------------

type NewITSetupProps = {
  fullName: string;
  existingEmployeeId?: string;
  existingEmployeeGroups?: Group[];
  isMultiLicense: boolean;
  organizationPackageId: string | undefined;
  hrisOnboardingRequestId: string;
  disableNext: boolean;
  addEmployee: (params: AddEmployeeParams) => void;
  submitOnboarding: (
    employeeId: string,
    onSuccess: () => void,
    needsHardware?: boolean,
    needsApplications?: boolean,
    watchers?: string[]
  ) => void;
  isApplicationAutomationEnabled?: boolean;
};

const OPTION_TO_STATE: Record<
  ITSetupOption,
  {
    needsApplications: boolean;
    needsHardware: boolean;
    licenseAction?: LicenseAction;
  }
> = {
  applicationsAndHardware: { needsApplications: true, needsHardware: true },
  applicationsOnly: { needsApplications: true, needsHardware: false },
  hardwareOnly: { needsApplications: false, needsHardware: true },
  grantLicenseOnly: {
    needsApplications: false,
    needsHardware: false,
    licenseAction: LicenseAction.GrantLicense,
  },
  noLicenseNeeded: {
    needsApplications: false,
    needsHardware: false,
    licenseAction: LicenseAction.NoLicense,
  },
};

const WatchersFieldWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;

  .arc-select__control {
    min-height: 6rem;
    background-color: ${getToken(ColorsV2.BACKGROUND_CONTENT)};
    border-radius: 8px;
  }

  .arc-select__multi-value {
    background-color: ${getToken(ColorsV2.PRIMARY_LIGHTEST)};
    border-radius: 999px;
  }

  .arc-select__multi-value__label {
    color: ${getToken(ColorsV2.PRIMARY_DARK)};
  }

  .arc-select__multi-value__remove {
    color: ${getToken(ColorsV2.PRIMARY_DARK)};

    &:hover {
      background-color: ${getToken(ColorsV2.PRIMARY_LIGHT)};
      color: ${getToken(ColorsV2.PRIMARY_DARK)};
    }
  }
`;

const BorderedCard = styled(Flex)`
  border: 1px solid ${getToken(ColorsV2.BORDER)};
  border-radius: 8px;
  background-color: ${getToken(ColorsV2.BACKGROUND_CONTENT)};
`;

const OptionTile = styled(Flex)`
  border: 1px solid ${getToken(ColorsV2.BORDER)};
  border-radius: 8px;
  background-color: ${getToken(ColorsV2.BACKGROUND_CONTENT)};
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background-color 120ms ease,
    box-shadow 120ms ease;

  &:hover {
    border-color: ${getToken(ColorsV2.PRIMARY)};
  }

  &[aria-checked='true'] {
    background-color: ${getToken(ColorsV2.PRIMARY_LIGHTEST)};
    border-color: ${getToken(ColorsV2.PRIMARY)};
    box-shadow: inset 0 0 0 1px ${getToken(ColorsV2.PRIMARY)};
  }
`;

const NewITSetup = ({
  fullName,
  existingEmployeeId,
  existingEmployeeGroups,
  isMultiLicense,
  organizationPackageId,
  hrisOnboardingRequestId,
  disableNext,
  addEmployee,
  submitOnboarding,
  isApplicationAutomationEnabled = false,
}: NewITSetupProps) => {
  const { t, i18n } = useTranslation(['common', 'pages']);
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const organizationId = useGetOrganizationId();
  const navigate = useNavigate();
  const { showErrorToast } = useGlobalUI();
  const [primaryColor, dividerColor] = useThemeColors([
    ColorsV2.PRIMARY,
    ColorsV2.BORDER,
  ]);
  const { control, setValue, trigger, getValues } =
    useFormContext<OnboardingFormData>();
  const { isValid, isSubmitting } = useFormState();
  const [
    jobTitle,
    startDate,
    manager,
    needsApplications,
    needsHardware,
    licenseAction,
    notifyManager,
    watchers,
  ] = useWatch({
    control,
    name: [
      'employeeInformation.jobTitle',
      'employeeInformation.startDate',
      'employeeInformation.manager',
      'needsApplications',
      'needsHardware',
      'licenseAction',
      'notifyManager',
      'watchers',
    ],
  });

  const isCurrentUserHardwareChampion = useIsRequestTypeCategoryChampion(
    RequestTypeCategoryNameEnum.DEVICE
  );

  const [bulkUpdateEmployees, { isLoading: isLoadingEmployees }] =
    useBulkUpdateEmployeesMutation();
  const { updateRequest, updateRequestLoading } = useUpdateRequestStatus({
    requestId: hrisOnboardingRequestId,
  });

  const employees = useOrganizationEmployees();
  const employeeOptions = transformEmployeesToOptions(
    employees,
    i18n.language,
    isAdpWfnCanada
  );

  const [selectedOption, setSelectedOption] = useState<ITSetupOption>(() => {
    // Derive initial selection from existing form state (e.g. when the user
    // navigates back to this step) so we don't lose their choice.
    if (needsApplications && needsHardware)
      return ITSetupOption.ApplicationsAndHardware;
    if (needsApplications) return ITSetupOption.ApplicationsOnly;
    if (needsHardware) return ITSetupOption.HardwareOnly;
    if (licenseAction === LicenseAction.GrantLicense)
      return ITSetupOption.GrantLicenseOnly;
    if (licenseAction === LicenseAction.NoLicense)
      return ITSetupOption.NoLicenseNeeded;
    // Default — recommended option pre-selected.
    return ITSetupOption.ApplicationsAndHardware;
  });

  // Sync form state with the default selection on first mount so submitters
  // downstream see consistent needsApplications / needsHardware flags.
  useEffect(() => {
    if (
      selectedOption === ITSetupOption.ApplicationsAndHardware &&
      !needsApplications &&
      !needsHardware &&
      !licenseAction
    ) {
      setValue('needsApplications', true);
      setValue('needsHardware', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectOption = (option: ITSetupOption) => {
    setSelectedOption(option);
    const next = OPTION_TO_STATE[option];
    setValue('needsApplications', next.needsApplications);
    setValue('needsHardware', next.needsHardware);
    setValue('licenseAction', next.licenseAction);
  };

  const handlePrevious = () => {
    if (isMultiLicense) {
      setValue('currentStep', OnboardingStep.LicenseSelection);
    } else {
      setValue('currentStep', OnboardingStep.EmployeeInformation);
    }
  };

  // Build the watcher list, appending manager id when the toggle is on.
  const buildWatchers = (): string[] => {
    const current = getValues('watchers') ?? [];
    const managerId = manager?.value;
    if (notifyManager && managerId) {
      return Array.from(new Set([...current, managerId]));
    }
    return current;
  };

  const ACTIVE_EMPLOYEES_PATH = '/active-employees';

  const navigateAfterSubmit = (employeeId: string) => {
    const hardwareStoreUrl = `/get-hardware?${SelectionParamKeys.EmployeeId}=${employeeId}`;
    return isCurrentUserHardwareChampion
      ? hardwareStoreUrl
      : ACTIVE_EMPLOYEES_PATH;
  };

  const showStatusUpdateError = () => {
    showErrorToast({
      id: 'it-setup-status-error-toast',
      message: t('pages:onboarding.errorUpdatingStatus'),
      stack: true,
    });
  };

  const handleNoLicense = async (employeeId?: string) => {
    try {
      if (existingEmployeeId) {
        await bulkUpdateEmployees({
          organizationId,
          employees: [
            { id: existingEmployeeId, status: EmployeeStatusEnum.Inactive },
          ],
        }).unwrap();
      }
      if (hrisOnboardingRequestId) {
        await updateRequest({ newStatus: RequestStatusEnum.Completed });
      }
      if (employeeId || existingEmployeeId) {
        navigate(ACTIVE_EMPLOYEES_PATH);
      }
    } catch (error) {
      showStatusUpdateError();
    }
  };

  // Handle the "grant license only" terminal action — set active without onboarding.
  const handleGrantLicense = async (employeeId: string) => {
    if (!employeeId) return;
    try {
      if (existingEmployeeId) {
        await bulkUpdateEmployees({
          organizationId,
          employees: [
            {
              id: existingEmployeeId,
              status: EmployeeStatusEnum.Active,
              organizationPackageId,
            },
          ],
        }).unwrap();
      }
      if (hrisOnboardingRequestId) {
        await updateRequest({ newStatus: RequestStatusEnum.Completed });
      }
      navigate(ACTIVE_EMPLOYEES_PATH);
    } catch (error) {
      showStatusUpdateError();
    }
  };

  const handleFinishOrNext = () => {
    if (!selectedOption) return;

    // Terminal: no license needed (single-license orgs only) → set inactive.
    if (selectedOption === ITSetupOption.NoLicenseNeeded) {
      if (!existingEmployeeId) {
        addEmployee({
          onSuccess: (employeeId: string) => {
            handleNoLicense(employeeId);
          },
          status: EmployeeStatusEnum.Inactive,
        });
      } else {
        handleNoLicense(existingEmployeeId);
      }
      return;
    }

    // Terminal: grant license only → activate without creating onboarding
    if (selectedOption === ITSetupOption.GrantLicenseOnly) {
      if (!existingEmployeeId) {
        addEmployee({
          onSuccess: (employeeId: string) => {
            handleGrantLicense(employeeId);
          },
          status: EmployeeStatusEnum.Active,
          createUser: true,
          organizationPackageId,
        });
      } else {
        handleGrantLicense(existingEmployeeId);
      }
      return;
    }

    // Hardware only is terminal too (creates onboarding, no Applications step).
    if (selectedOption === ITSetupOption.HardwareOnly) {
      const finalWatchers = buildWatchers();
      const onEmployee = (employeeId: string) => {
        submitOnboarding(
          employeeId,
          () => navigate(navigateAfterSubmit(employeeId)),
          true,
          false,
          finalWatchers
        );
      };
      if (!existingEmployeeId) {
        addEmployee({
          onSuccess: onEmployee,
          status: EmployeeStatusEnum.Active,
          organizationPackageId,
        });
      } else {
        onEmployee(existingEmployeeId);
      }
      return;
    }

    // Applications-bearing flows continue to the Applications step.
    if (!existingEmployeeId) {
      addEmployee({
        onSuccess: () => setValue('currentStep', OnboardingStep.Applications),
        status: EmployeeStatusEnum.Active,
        organizationPackageId,
      });
    } else {
      setValue('currentStep', OnboardingStep.Applications);
    }
  };

  const isLicenseTerminalOption =
    selectedOption === ITSetupOption.GrantLicenseOnly ||
    selectedOption === ITSetupOption.NoLicenseNeeded;

  const isTerminalOption =
    isLicenseTerminalOption ||
    (selectedOption === ITSetupOption.HardwareOnly &&
      !isCurrentUserHardwareChampion);

  const handleAutomatedApplicationsFinish = () => {
    if (!existingEmployeeId) return;

    const finalWatchers = buildWatchers();
    submitOnboarding(
      existingEmployeeId,
      () => {
        const destination =
          needsHardware && isCurrentUserHardwareChampion
            ? `/get-hardware?${SelectionParamKeys.EmployeeId}=${existingEmployeeId}`
            : `/employees/${existingEmployeeId}`;
        navigate(destination);
      },
      needsHardware,
      true,
      finalWatchers
    );
  };

  const primaryButtonLabel = isApplicationAutomationEnabled
    ? t('onboardings.onboarding.finishButton')
    : isTerminalOption
      ? t('onboardings.onboarding.finishButton')
      : t('onboardings.onboarding.nextButton');
  const disablePrimary =
    !selectedOption ||
    !isValid ||
    (disableNext && selectedOption !== ITSetupOption.NoLicenseNeeded) ||
    isSubmitting;

  const formattedStartDate = startDate
    ? formatDate(startDate, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const subtitleParts = [
    jobTitle,
    formattedStartDate
      ? t('onboardings.onboarding.startsOn', { date: formattedStartDate })
      : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const chips = (existingEmployeeGroups ?? []).filter(g => !!g?.name);

  return (
    <>
      <Header title={t('onboardings.onboarding.newTitle', { fullName })} />
      {(subtitleParts || chips.length > 0) && (
        <Flex
          alignItems="center"
          gap={spaces.space_xs}
          mt={spaces.space_xs}
          mb={spaces.space_l}
          flexWrap="wrap">
          {subtitleParts && (
            <Text variant="body" intent="secondary">
              {subtitleParts}
            </Text>
          )}
          {chips.map(group => (
            <Chip
              id={`it-setup-group-chip-${group.id}`}
              key={group.id}
              variant="small">
              {group.name}
            </Chip>
          ))}
        </Flex>
      )}
      <Text variant="body" mb={spaces.space_m}>
        {t('onboardings.onboarding.newSubtitle')}
      </Text>

      {isApplicationAutomationEnabled && (
        <BorderedCard
          id="it-setup-applications-automated-summary"
          alignItems="center"
          gap={spaces.space_m}
          p={spaces.space_m}
          mb={spaces.space_m}>
          <OptionIcon iconName="cards" selected large />
          <Text variant="label-large">
            {t(
              'pages:companySettings.employeeUpdates.itOnboarding.applications.automated.label'
            )}
          </Text>
        </BorderedCard>
      )}

      {/* Recommended option (full-width) */}
      <OptionTile
        id="it-setup-option-applications-and-hardware"
        role="radio"
        aria-checked={selectedOption === ITSetupOption.ApplicationsAndHardware}
        onClick={() =>
          handleSelectOption(ITSetupOption.ApplicationsAndHardware)
        }
        width="100%"
        alignItems="center"
        gap={spaces.space_m}
        flexWrap="wrap"
        p={spaces.space_l}>
        <OptionIcon
          iconName="cards"
          selected={selectedOption === ITSetupOption.ApplicationsAndHardware}
          large
        />
        <Flex flexDirection="column" gap={spaces.space_3xs} flex="1">
          <Flex alignItems="center" gap={spaces.space_xs} flexWrap="wrap">
            <Text variant="heading-3">
              {t(
                'onboardings.onboarding.options.applicationsAndHardware.label'
              )}
            </Text>
            <Chip
              id="it-setup-recommended-chip"
              variant="small"
              textColor={primaryColor as string}>
              {t('onboardings.onboarding.recommended')}
            </Chip>
          </Flex>
          <Text variant="body-2" intent="secondary">
            {t(
              'onboardings.onboarding.options.applicationsAndHardware.description'
            )}
          </Text>
        </Flex>
      </OptionTile>

      {/* Other options arranged in a responsive grid */}
      <Flex mt={spaces.space_m} gap={spaces.space_m} flexWrap="wrap">
        {(isApplicationAutomationEnabled
          ? [
              {
                key: ITSetupOption.ApplicationsOnly,
                iconName: 'app' as const,
              },
            ]
          : [
              {
                key: ITSetupOption.ApplicationsOnly,
                iconName: 'app' as const,
              },
              {
                key: ITSetupOption.HardwareOnly,
                iconName: 'monitor' as const,
              },
              {
                key: ITSetupOption.GrantLicenseOnly,
                iconName: 'circle-xmark' as const,
              },
            ]
        ).map(({ key, iconName }) => (
          <OptionTile
            key={key}
            id={`it-setup-option-${key}`}
            role="radio"
            aria-checked={selectedOption === key}
            onClick={() => handleSelectOption(key)}
            flex="1 1 22rem"
            minWidth={sizes.size_56}
            alignItems="center"
            gap={spaces.space_m}
            p={spaces.space_m}>
            <OptionIcon iconName={iconName} selected={selectedOption === key} />
            <Flex flexDirection="column" gap={spaces.space_3xs} flex="1">
              <Text variant="subheading">
                {t(`onboardings.onboarding.options.${key}.label`)}
              </Text>
              <Text variant="body-2" intent="secondary">
                {t(`onboardings.onboarding.options.${key}.description`)}
              </Text>
            </Flex>
          </OptionTile>
        ))}
        {!isMultiLicense && !isApplicationAutomationEnabled && (
          <OptionTile
            id="it-setup-option-noLicenseNeeded"
            role="radio"
            aria-checked={selectedOption === ITSetupOption.NoLicenseNeeded}
            onClick={() => handleSelectOption(ITSetupOption.NoLicenseNeeded)}
            flex="1 1 22rem"
            minWidth={sizes.size_56}
            alignItems="center"
            gap={spaces.space_m}
            p={spaces.space_m}>
            <OptionIcon
              iconName="alert-circle"
              selected={selectedOption === ITSetupOption.NoLicenseNeeded}
            />
            <Flex flexDirection="column" gap={spaces.space_3xs} flex="1">
              <Text variant="subheading">
                {t('onboardings.onboarding.options.noLicenseNeeded.label')}
              </Text>
              <Text variant="body-2" intent="secondary">
                {t(
                  'onboardings.onboarding.options.noLicenseNeeded.description'
                )}
              </Text>
            </Flex>
          </OptionTile>
        )}
      </Flex>

      {!isLicenseTerminalOption && (
        <>
          <Divider
            width="100%"
            space={spaces.space_xl}
            color={dividerColor as string}
          />

          {/* Notifications section */}
          <Text variant="heading-2">
            {t('onboardings.onboarding.notifications.heading')}
          </Text>

          {manager && (
            <>
              <Text variant="subheading" mt={spaces.space_m}>
                {t('onboardings.onboarding.notifications.managerHeading')}
              </Text>
              <Text variant="body-2" intent="secondary" mb={spaces.space_s}>
                {t('onboardings.onboarding.notifications.managerDescription')}
              </Text>
              <BorderedCard
                alignItems="center"
                justifyContent="space-between"
                gap={spaces.space_m}
                p={spaces.space_m}>
                {(() => {
                  const managerEmployee = employees.find(
                    e => e.id === manager.value
                  );
                  const jobTitle = managerEmployee?.jobTitle;
                  const managerEmail = managerEmployee?.email;
                  const subline = [jobTitle, managerEmail]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <Flex alignItems="center" gap={spaces.space_m}>
                      <InitialsInCircle text={manager.label} />
                      <Flex flexDirection="column" gap={spaces.space_3xs}>
                        <Text variant="label-large">{manager.label}</Text>
                        {subline && (
                          <Text variant="body-2" intent="secondary">
                            {subline}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  );
                })()}
                <Switch
                  id="onboarding-notify-manager-switch"
                  checked={!!notifyManager}
                  onChange={() => setValue('notifyManager', !notifyManager)}
                />
              </BorderedCard>
            </>
          )}

          <Text variant="subheading" mt={spaces.space_l}>
            {t('onboardings.onboarding.notifications.additionalPeopleHeading')}
          </Text>
          <Text variant="body-2" intent="secondary" mb={spaces.space_s}>
            {t(
              'onboardings.onboarding.notifications.additionalPeopleDescription'
            )}
          </Text>
          <WatchersFieldWrapper>
            <SearchableMultiSelectField
              id="onboarding-watchers"
              name="watchers"
              control={control}
              trigger={trigger}
              label=""
              placeholder={t(
                (watchers ?? []).length === 0
                  ? 'onboardings.onboarding.notifications.additionalPeoplePlaceholderEmpty'
                  : 'onboardings.onboarding.notifications.additionalPeoplePlaceholder'
              )}
              options={employeeOptions}
              customComponents={{
                Option: option => <EmployeeSelectOption data={option.data} />,
              }}
              searchableFields={['name', 'email']}
            />
          </WatchersFieldWrapper>
        </>
      )}

      <FormActionsContainer>
        <Button
          id="onboarding-previous-button"
          variant="outline"
          onClick={handlePrevious}
          disabled={
            isSubmitting ||
            isApplicationAutomationEnabled ||
            (!!existingEmployeeId && !isMultiLicense)
          }>
          {t('onboardings.onboarding.previousButton')}
        </Button>
        <Button
          id="onboarding-next-button"
          onClick={
            isApplicationAutomationEnabled
              ? handleAutomatedApplicationsFinish
              : handleFinishOrNext
          }
          loading={isSubmitting || updateRequestLoading || isLoadingEmployees}
          disabled={disablePrimary}>
          {primaryButtonLabel}
        </Button>
      </FormActionsContainer>
    </>
  );
};

const OptionIcon = ({
  iconName,
  selected,
  large = false,
}: {
  iconName: IconNames;
  selected: boolean;
  large?: boolean;
}) => {
  const [bg] = useThemeColors([ColorsV2.GRAY_LIGHTER]);
  const containerSize = large ? sizes.size_56 : sizes.size_40;
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      width={containerSize}
      height={containerSize}
      borderRadius={sizes.size_8}
      flexShrink={0}
      backgroundColor={bg as string}>
      <Icon
        icon={iconName}
        size={large ? 'medium' : 'small'}
        intent={selected ? 'primary' : 'neutral'}
      />
    </Flex>
  );
};

export default Onboarding;
